import * as THREE from 'three';
import { ProjectilePool } from './projectiles';

/** Chest-height muzzle offset — matches OrbitCamera's own TARGET_HEIGHT convention for "roughly chest height." */
const MUZZLE_HEIGHT = 1.0;

export type WeaponPattern = 'projectile' | 'pellets' | 'melee';
export type WeaponId = 'nine' | 'sawedoff' | 'stickball';
export const WEAPON_ORDER: WeaponId[] = ['nine', 'sawedoff', 'stickball'];

export interface WeaponDef {
  id: WeaponId;
  label: string;
  pattern: WeaponPattern;
  cooldown: number;
  /** Per-shot (projectile), per-pellet (pellets), or per-swing (melee) base damage — pre-crit. */
  damage: number;
  critChance: number;
  critMultiplier: number;
  knockbackForce: number;
  color: number;
  /**
   * The single "range" stat surfaced to the HUD/roster comparison (Karan's
   * playtest ask: "higher range or power, a reason to use one weapon over
   * another"). For projectile/pellets it DRIVES the fired shot's lifetime
   * (`range / projectileSpeed`), so tuning range alone can't desync from
   * how far the projectile actually travels. For melee it IS the swing
   * radius directly.
   */
  range: number;
  // projectile / pellets only
  projectileSpeed: number;
  projectileRadius: number;
  pelletCount: number;
  /** Total cone half-angle (radians) the pellets fan across — pellets only. */
  pelletSpreadRad: number;
  // melee only
  meleeArcHalfAngle: number;
}

/**
 * The weapon pattern families PLAN-CITY §5 calls for, ported from Classic's
 * per-pattern weapon system (Classic's `wDmg`/pattern dispatch) at a small
 * roster scale appropriate to this slice — 3 weapons, one per pattern, each
 * with a genuine range/power tradeoff so switching is a real choice, not a
 * cosmetic. Real unlock-gating (crafted/earned, not free from the start) is
 * S10/S11's bodega/craft-bench territory — every weapon here ships unlocked
 * so the SWITCH mechanic itself (Karan's playtest ask) can be built and
 * tuned now, without inventing a throwaway unlock system this slice would
 * just have to replace later.
 */
export const WEAPONS: Record<WeaponId, WeaponDef> = {
  nine: {
    id: 'nine',
    label: 'The Nine',
    pattern: 'projectile',
    cooldown: 0.22, // ~4.5 shots/sec — matches Classic's fast starter-pistol feel
    damage: 12,
    critChance: 0.08,
    critMultiplier: 2,
    knockbackForce: 2.4,
    color: 0xfff2a8,
    range: 16,
    projectileSpeed: 22,
    projectileRadius: 0.12,
    pelletCount: 1,
    pelletSpreadRad: 0,
    meleeArcHalfAngle: 0,
  },
  sawedoff: {
    id: 'sawedoff',
    label: 'Sawed-Off',
    pattern: 'pellets',
    cooldown: 0.62,
    damage: 7, // per pellet — up to 35 in one burst at point-blank, the whole point of a short-range weapon
    critChance: 0.05,
    critMultiplier: 1.8,
    knockbackForce: 3.2,
    color: 0xff8a3d,
    range: 7, // pellets lose all cohesion well before The Nine's own range — a genuinely short-range weapon, not just a reskin
    projectileSpeed: 18,
    projectileRadius: 0.1,
    pelletCount: 5,
    pelletSpreadRad: 0.34, // ~19.5° cone half-angle
    meleeArcHalfAngle: 0,
  },
  stickball: {
    id: 'stickball',
    label: 'Stickball Bat',
    pattern: 'melee',
    cooldown: 0.45,
    damage: 22, // highest single-hit damage in the roster — the payoff for closing to melee range
    critChance: 0.12,
    critMultiplier: 2.2,
    knockbackForce: 6.5,
    color: 0xc9c9c9,
    range: 2, // meleeRange — the swing radius itself
    projectileSpeed: 0,
    projectileRadius: 0,
    pelletCount: 0,
    pelletSpreadRad: 0,
    meleeArcHalfAngle: 0.95, // ~54° either side of facing — a real arc swing, not a pinpoint stab
  },
};

/** What `WeaponSystem.tryFire` hands back for a melee swing — projectile/pellets fire straight into the pool and return null instead, since resolution for those happens later when the projectile actually connects. */
export interface MeleeSwing {
  damage: number;
  crit: boolean;
  range: number;
  arcHalfAngle: number;
  knockbackForce: number;
}

function spreadDirection(aimDir: THREE.Vector3, halfSpread: number, index: number, count: number): THREE.Vector3 {
  const t = count <= 1 ? 0 : (index / (count - 1)) * 2 - 1; // -1..1 across the pellet count
  const angle = t * halfSpread;
  return aimDir.clone().normalize().applyAxisAngle(new THREE.Vector3(0, 1, 0), angle);
}

/**
 * Owns rate-of-fire and dispatches by `WeaponDef.pattern`. `currentId` is the
 * equipped weapon; switching does NOT reset the in-flight cooldown (closes an
 * obvious switch-to-cancel-cooldown exploit — the new weapon simply inherits
 * whatever time is already ticking down, same as Classic never let a weapon
 * swap grant a free instant shot either).
 */
export class WeaponSystem {
  currentId: WeaponId;
  private cooldown = 0;
  /**
   * S07: true iff the MOST RECENT `tryFire()` call actually fired (either
   * pattern) — false if it declined because the cooldown hadn't elapsed.
   * `tryFire`'s own `MeleeSwing | null` return can't distinguish "declined"
   * from "fired a projectile/pellets" (both return `null`), so this is a
   * purely ADDITIVE field alongside the existing contract, not a change to
   * it — lets main.ts play a 'shoot' sfx only on a genuine fire without
   * duplicating the cooldown check or reaching into private state.
   */
  didFireLastCall = false;

  constructor(initial: WeaponId = 'nine') {
    this.currentId = initial;
  }

  get current(): WeaponDef {
    return WEAPONS[this.currentId];
  }

  setWeapon(id: WeaponId): void {
    this.currentId = id;
  }

  update(dt: number): void {
    if (this.cooldown > 0) this.cooldown -= dt;
  }

  /**
   * Fires the equipped weapon if its cooldown has elapsed. Projectile/pellets
   * patterns fire directly into `pool` and return `null`; a melee pattern
   * fires nothing into the pool and instead returns a `MeleeSwing` for the
   * caller to resolve immediately via `combat/resolve.ts`'s
   * `resolveMeleePlayerHits` — melee has no travel time, so there's no later
   * "resolution" moment the way a projectile has.
   */
  tryFire(originXZ: THREE.Vector3, aimDir: THREE.Vector3, pool: ProjectilePool): MeleeSwing | null {
    this.didFireLastCall = false;
    if (this.cooldown > 0) return null;
    const def = this.current;
    this.cooldown = def.cooldown;
    this.didFireLastCall = true;

    if (def.pattern === 'melee') {
      const crit = Math.random() < def.critChance;
      // Fire-time-capture (§0.7): the final crit-applied damage is baked
      // HERE, at the moment the swing is thrown — resolve.ts never re-rolls it.
      const damage = crit ? def.damage * def.critMultiplier : def.damage;
      return { damage, crit, range: def.range, arcHalfAngle: def.meleeArcHalfAngle, knockbackForce: def.knockbackForce };
    }

    const origin = new THREE.Vector3(originXZ.x, originXZ.y + MUZZLE_HEIGHT, originXZ.z);
    const shots = def.pattern === 'pellets' ? def.pelletCount : 1;
    const life = def.range / def.projectileSpeed;
    for (let i = 0; i < shots; i++) {
      const crit = Math.random() < def.critChance;
      const damage = crit ? def.damage * def.critMultiplier : def.damage; // baked at fire time, same law as melee above
      const dir = shots === 1 ? aimDir.clone() : spreadDirection(aimDir, def.pelletSpreadRad, i, shots);
      pool.fire({
        owner: 'player',
        origin,
        velocity: dir.multiplyScalar(def.projectileSpeed),
        damage,
        knockbackForce: def.knockbackForce,
        radius: def.projectileRadius,
        life,
        color: def.color,
        crit,
      });
    }
    return null;
  }
}
